import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { ReactNode, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { maskValue } from "./JailTextInputWrapper";
import { useIsTextMasked } from "@/app/dashboard/history/children/useIsDataMasked";

const SEARCH_DELAY = 500;

const ResultsList = ({ children }: { children: ReactNode }) => {
  return (
    <UnorderedList
      position="absolute"
      boxShadow="md"
      bg="white"
      borderRadius="md"
      zIndex="1000"
      listStyleType="none"
      p="0px"
      pt="2"
      m="0px"
      w="100%"
    >
      {children}
    </UnorderedList>
  );
};

const ConditonsInput = ({
  value = [],
  onChange,
}: {
  value: string[];
  onChange: Function;
}) => {
  const isTextMasked = useIsTextMasked();

  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const searchConditions = async (term: string) => {
    const { data } = await axios.get(
      `https://clinicaltables.nlm.nih.gov/api/conditions/v3/search?terms=${term}`
    );
    setResults(data[3]);
    setResultsLoading(false);
  };

  const addSelected = (val: [string]) => {
    onChange([...value, val[0]]);
    setResults([]);
    setSearchTerm("");
  };

  const deleteCondition = (record: string) => {
    onChange(value.filter((item) => item.trim() !== record.trim()));
  };

  useEffect(() => {
    if (searchTerm) {
      setResultsLoading(true);
    }
    const handler = setTimeout(() => {
      searchConditions(searchTerm);
    }, SEARCH_DELAY);

    return () => {
      setResultsLoading(false);
      clearTimeout(handler);
    };
  }, [searchTerm]);

  return (
    <VStack w="100%">
      <Box position="relative" w="100%">
        <VStack mt="16px">
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search for conditions..."
              value={searchTerm}
              size="lg"
              onChange={(ev) => setSearchTerm(ev.target.value)}
            />
          </InputGroup>
        </VStack>

        {resultsLoading && (
          <ResultsList>
            <ListItem px="4" py="2" textAlign="center">
              <Spinner size="sm" />
            </ListItem>
          </ResultsList>
        )}

        {searchTerm && !resultsLoading && (
          <ResultsList>
            {results.map((result) => (
              <ListItem
                px="4"
                py="2"
                key={result[0]}
                cursor="pointer"
                _hover={{
                  bg: "gray.100",
                }}
                onClick={() => addSelected(result)}
              >
                {result[0]}
              </ListItem>
            ))}
            {results.length === 0 && (
              <ListItem
                px="4"
                py="2"
                cursor="pointer"
                _hover={{
                  bg: "gray.100",
                }}
                onClick={() => addSelected([searchTerm])}
              >
                {searchTerm}
              </ListItem>
            )}
          </ResultsList>
        )}
      </Box>

      {value.map((condition) => (
        <HStack
          key={condition}
          alignItems="center"
          w="100%"
          borderBottomWidth="1px"
          borderBottomColor="gray.200"
        >
          <Flex flexGrow="1">
            <Text key={condition}>
              {isTextMasked ? maskValue(condition) : condition}
            </Text>
          </Flex>
          <Flex>
            <IconButton
              aria-label="Remove Condition"
              icon={<FaTimes />}
              variant="ghost"
              onClick={() => deleteCondition(condition)}
            />
          </Flex>
        </HStack>
      ))}
    </VStack>
  );
};

export default ConditonsInput;
